import {NgModule} from '@angular/core';
import {Routes, RouterModule} from '@angular/router';
import {HomeComponent} from './pages/home/home.component';
import {ProductsComponent} from './pages/products/products.component';
import {DashboardComponent} from './pages/dashboard/dashboard.component';
import {ManageHouseholdComponent} from './pages/manage-household/manage-household.component';
import {ProfileComponent} from './pages/profile/profile.component';
import {ScanComponent} from './pages/scan/scan.component';
import {ProductDetailsComponent} from './pages/product-details/product-details.component';
import {InvitationHandlerComponent} from './pages/invitation-handler/invitation-handler.component';
import {NotFoundComponent} from './pages/not-found/not-found.component';
import {AuthGuard} from './auth/auth.guard.service';
import {LandingComponent} from './pages/landing/landing.component';
import {ReceiptsComponent} from "./pages/receipts/receipts.component";
import {ReceiptDetailComponent} from "./pages/receipt-detail/receipt-detail.component";
import {InvitationPreviewComponent} from "./pages/invitation-preview/invitation-preview.component";
import {InvitationFinalizeComponent} from "./pages/invitation-finalize/invitation-finalize.component";
import {ForbiddenComponent} from "./pages/forbidden/forbidden.component";
import {ExpiringProductsComponent} from "./pages/expiring-products/expiring-products.component";
import {ManageRecipeComponent} from "./pages/manage-recipe/manage-recipe.component";
import {unsavedChangesGuard} from "./pages/manage-recipe/unsaved-changes.guard";
import {RecipeEditorDebuggerComponent} from "./pages/recipe-editor-debugger/recipe-editor-debugger.component";
import {RecipesComponent} from "./pages/recipes/recipes.component";
import {RecipeComponent} from "./pages/recipe/recipe.component";
import {ShoppingListExportComponent} from "./pages/shopping-list-export/shopping-list-export.component";
import {ShoppingComponent} from "./pages/shopping/shopping.component";
import {AddNewProductComponent} from "./pages/product/add-new-product.component";
import {EditProductComponent} from "./pages/product/edit-product.component";
import {ManageDefaultHouseholdComponent} from "./pages/manage-default-household/manage-default-household.component";
import {MealPlannerComponent} from "./pages/meal-planner/meal-planner.component";

const routes: Routes = [
  {path: '', redirectTo: '/login', pathMatch: 'full'},
  {path: 'login', component: LandingComponent},
  {path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Dashboard'}},
  {path: 'scan', component: ScanComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Scan Mode'}},
  {path: 'receipts', component: ReceiptsComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Receipt Imports'}},
  {path: 'receipts/:id', component: ReceiptDetailComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Receipt Details'}},
  {path: 'households/:id/manage', component: ManageHouseholdComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Manage Household'}},
  {path: 'products/new', component: AddNewProductComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Add Product'}},
  {path: 'products/:id/edit', component: EditProductComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Edit Product'}},
  {path: 'products/:id', component: ProductDetailsComponent, canActivate: [AuthGuard]},
  {path: 'products', component: ProductsComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Products'}},
  {path: 'expiring-products', component: ExpiringProductsComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Expiring Products'}},
  {
    path: 'quick-check',
    loadComponent: () => import('./pages/quick-check/quick-check.component').then(m => m.QuickCheckComponent),
    canActivate: [AuthGuard],
    data: {breadcrumb: 'Quick Check'}
  },
  {path: 'invitation/preview', component: InvitationPreviewComponent},
  {path: 'invitation/finalize', component: InvitationFinalizeComponent},
  {path: 'recipe/debug', component: RecipeEditorDebuggerComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Recipe Debugger'}},
  {path: 'recipe/new', component: ManageRecipeComponent, canActivate: [AuthGuard], canDeactivate: [unsavedChangesGuard], data: {breadcrumb: 'New Recipe'}},
  {path: 'recipe/:id/edit', component: ManageRecipeComponent, canActivate: [AuthGuard], canDeactivate: [unsavedChangesGuard], data: {breadcrumb: 'Edit Recipe'}},
  {path: 'recipe/:id', component: RecipeComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Recipe'}},
  {path: 'recipes', component: RecipesComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Recipes'}},
  {path: 'recipes/import', component: RecipesComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Import Recipe'}},
  {path: 'meal-plan', component: MealPlannerComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Meal Planner'}},
  {path: 'shopping', component: ShoppingComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Shopping'}},
  {path: 'shopping-list-export', component: ShoppingListExportComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Shopping Lists'}},
  {path: 'manage-household', component: ManageDefaultHouseholdComponent, canActivate: [AuthGuard]},
  {path: 'invitations/:id/:action', component: InvitationHandlerComponent, canActivate: [AuthGuard]},
  {
    path: 'profile', component: ProfileComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Profile'}, children: [
      {
        path: 'household',
        component: ManageHouseholdComponent,
        data: {breadcrumb: 'Household'}
      }
    ]
  },
  {path: 'home', component: HomeComponent, canActivate: [AuthGuard], data: {breadcrumb: 'Home'}},
  {path: 'error/forbidden', component: ForbiddenComponent},
  // ── shadcn/ui look-and-feel spike: standalone, no shell/guard — see app.component.html ──
  {
    path: 'shadcn',
    loadChildren: () =>
      import('./pages/shadcn-demo/shadcn-demo.routes').then((m) => m.SHADCN_ROUTES),
  },
  // ── Admin area (lazy-loaded, guarded by adminGuard inside the child routes) ──
  {
    path: 'admin',
    loadChildren: () =>
      import('./admin/admin.routes').then((m) => m.ADMIN_ROUTES),
  },
  {path: '**', component: NotFoundComponent}
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
